import React from "react";

import {
  CheckCircle2,
  Clock,
  FileText,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { LeaseContractDetails } from "js/components/Tenants/TenantReportDetails/typing";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "js/components/ui/card";
import {
  DescriptionList,
  DescriptionItem,
} from "js/components/ui/description-list";
import { StatusBadge } from "js/components/ui/status-badge";
import { Separator } from "js/components/ui/separator";
import { Button } from "js/components/ui/button";

import { useIsMobile } from "../../../../hooks/useMediaQuery";
import { formatCurrency } from "../../../../utils/utils";
import ReportSubBlock from "./ReportSubBlock";
import { getContractStatusConfig } from "./utils";

dayjs.extend(customParseFormat);

interface LeaseContractDetailsProps {
  lease_contract: LeaseContractDetails;
  /** Render as a collapsible sub-block (design 8a) instead of a standalone card. */
  asSubBlock?: boolean;
  /** Report folio — used in the "request contract" WhatsApp message when empty. */
  folio?: string;
  /** True when the expediente already carries a lease-contract-* subfolder — i.e. the contract
   *  was already requested. Distinguishes "requested but not yet filled" from "not requested". */
  contractRequested?: boolean;
}

const LeaseContractDetailsComponent: React.FC<LeaseContractDetailsProps> = ({
  lease_contract,
  asSubBlock = false,
  folio,
  contractRequested = false,
}) => {
  const isMobile = useIsMobile();
  const signedDate = lease_contract?.signed_at ?? lease_contract?.closed_at ?? null;

  const isSigned =
    lease_contract?.status === "signed" || !!signedDate;

  const formatSignedDate = (value: string) => {
    const parsed = dayjs(value, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY"], true);

    if (parsed.isValid()) {
      return parsed.format("DD/MM/YYYY");
    }

    const fallbackParsed = dayjs(value);
    return fallbackParsed.isValid() ? fallbackParsed.format("DD/MM/YYYY") : value;
  };

  const getPhaseStatus = () => {
    // Source of truth: the Contract model's state machine (created → payment_in_progress
    // → signature_in_progress → signed / cancelled) linked to the report. Fall back to
    // "in_progress" only when there's no contract yet (multiexpediente-only, data
    // validated but no signable document generated).
    return lease_contract?.status ?? "in_progress";
  };

  const getRenewalStatus = () => {
    if (!lease_contract?.end_date || !isSigned) {
      return null;
    }

    const today = dayjs().startOf("day");
    const parsed = dayjs(lease_contract.end_date, "DD/MM/YYYY", true).isValid()
      ? dayjs(lease_contract.end_date, "DD/MM/YYYY", true)
      : dayjs(lease_contract.end_date);
    const endDate = parsed.endOf("day");
    const daysUntilExpiry = endDate.diff(today, "day");

    if (daysUntilExpiry < 0) {
      return { status: "expired" as const, days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 30) {
      return { status: "nearing" as const, days: daysUntilExpiry };
    } else {
      return { status: "active" as const, days: daysUntilExpiry };
    }
  };

  const renewalStatus = getRenewalStatus();
  const phaseConfig = getContractStatusConfig(getPhaseStatus());
  const renewalConfig = renewalStatus
    ? getContractStatusConfig(renewalStatus.status)
    : null;

  // Case 3: neither a Contract model nor lease-contract-pf/pm data exists.
  const hasAnyContractData = !!(
    lease_contract?.status ||
    lease_contract?.start_date ||
    lease_contract?.end_date ||
    lease_contract?.payment_day ||
    lease_contract?.grace_period ||
    lease_contract?.payment_method ||
    lease_contract?.guarantee_deposit
  );

  const requestContractViaWhatsApp = () => {
    const phoneNumber = "5213333324037";
    const message = `Hola, solicito la generación del contrato de arrendamiento para la Solicitud${
      folio ? ` con folio ${folio}` : ""
    }.`;
    window.open(
      `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  // Show the "request a contract" CTA only when the contract was NOT requested (no
  // lease-contract-* subfolder in the expediente). When it WAS requested but its data isn't
  // captured yet, fall through to the normal fields view — each field renders "Sin datos".
  if (asSubBlock && !hasAnyContractData && !contractRequested) {
    return (
      <ReportSubBlock
        icon={FileText}
        title="Contrato"
        badge={
          <StatusBadge status="neutral" label="Sin generar" showDot={false} />
        }
        divider
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <ShieldCheck className="size-5" />
            </div>
            <div className="min-w-0">
              <p className="text-base font-bold text-foreground">
                Protege tu arrendamiento
              </p>
              <p className="text-sm text-muted-foreground">
                Genera un contrato con respaldo legal en un solo clic.
              </p>
            </div>
          </div>
          <Button
            onClick={requestContractViaWhatsApp}
            className="w-full font-semibold"
          >
            <MessageCircle className="size-4" />
            Solicitar contrato por WhatsApp
          </Button>
          <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <CheckCircle2 className="size-3.5" />
            Respaldo jurídico del IJA · listo en minutos
          </div>
        </div>
      </ReportSubBlock>
    );
  }

  const fields = (
    <DescriptionList>
      <DescriptionItem label="Inicio de Vigencia">
        {lease_contract?.start_date ?? "Sin datos"}
      </DescriptionItem>
      <DescriptionItem label="Fin de Vigencia">
        <span
          className={
            renewalStatus?.status === "expired"
              ? "text-red-600"
              : renewalStatus?.status === "nearing"
                ? "text-amber-600"
                : ""
          }
        >
          {lease_contract?.end_date ?? "Sin datos"}
        </span>
      </DescriptionItem>
      <DescriptionItem label="Fecha de firma">
        {signedDate ? formatSignedDate(signedDate) : "Sin firmar"}
      </DescriptionItem>
      <DescriptionItem label="Día de pago mensual">
        {lease_contract?.payment_day
          ? `${lease_contract.payment_day} de cada mes`
          : "Sin datos"}
      </DescriptionItem>
      <DescriptionItem label="Días de gracia">
        {lease_contract?.grace_period
          ? `${lease_contract.grace_period} días`
          : "Sin datos"}
      </DescriptionItem>
      <DescriptionItem label="Depósito en garantía">
        {formatCurrency(lease_contract?.guarantee_deposit) ?? null}
      </DescriptionItem>
      <DescriptionItem label="Forma de pago">
        {lease_contract?.payment_method
          ? lease_contract.payment_method === "cash"
            ? "Efectivo"
            : "Transferencia"
          : "Sin datos"}
      </DescriptionItem>
    </DescriptionList>
  );

  // Primary status → header badge, semantically consistent with the Descargas sub-block
  // (whose "X de Y listos" badge also sits in the header).
  const phaseBadge = (
    <StatusBadge
      status={phaseConfig.variant}
      label={phaseConfig.label}
      icon={phaseConfig.icon}
      showDot={false}
    />
  );

  const renewalRow =
    renewalConfig ||
    (isSigned && renewalStatus && renewalStatus.status !== "expired") ? (
      <div className="flex flex-wrap items-center gap-2">
        {renewalConfig && (
          <StatusBadge
            status={renewalConfig.variant}
            label={renewalConfig.label}
            icon={renewalConfig.icon}
            showDot={false}
          />
        )}
        {isSigned && renewalStatus && renewalStatus.status !== "expired" && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="size-3.5" />
            {`${renewalStatus.days} días para renovación`}
          </span>
        )}
      </div>
    ) : null;

  if (asSubBlock) {
    return (
      <ReportSubBlock
        icon={FileText}
        title="Contrato"
        badge={phaseBadge}
        divider
      >
        {renewalRow ? (
          <div className="space-y-3">
            {renewalRow}
            {fields}
          </div>
        ) : (
          fields
        )}
      </ReportSubBlock>
    );
  }

  return (
    <Card className="mb-2">
      <CardHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <FileText className="size-4 text-muted-foreground" />
            <CardTitle>Detalles del Contrato de Arrendamiento</CardTitle>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Fase:</span>
            <StatusBadge
              status={phaseConfig.variant}
              label={phaseConfig.label}
              icon={phaseConfig.icon}
              showDot={false}
            />

            {renewalConfig && (
              <>
                {!isMobile && (
                  <Separator orientation="vertical" className="mx-1 h-4" />
                )}
                <span className="text-sm text-muted-foreground">Estado:</span>
                <StatusBadge
                  status={renewalConfig.variant}
                  label={renewalConfig.label}
                  icon={renewalConfig.icon}
                  showDot={false}
                />
              </>
            )}

            {isSigned &&
              renewalStatus &&
              renewalStatus.status !== "expired" && (
                <>
                  {!isMobile && (
                    <Separator orientation="vertical" className="mx-1 h-4" />
                  )}
                  <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Clock className="size-3.5" />
                    {`${renewalStatus.days} días para renovación`}
                  </span>
                </>
              )}
          </div>
        </div>
      </CardHeader>

      <Separator className="mx-6 w-auto" />

      <CardContent>{fields}</CardContent>
    </Card>
  );
};

export default LeaseContractDetailsComponent;
